import type { DocumentData, Query, QuerySnapshot } from 'firebase/firestore'
import { onSnapshot, query, startAfter, and, where } from 'firebase/firestore'
import { LiveService } from './serviceLive'
import type { FirestoreType } from '~/models/firestoreType'
import type { Controller } from '~/features/controller'
import { ControllerStateArray } from '~/features/controllerStateArray'
import type { DateRange } from './interfaces/dateRange'

export class PaginatedArrayService<
  DataType extends FirestoreType,
> extends LiveService<DataType, DataType[]> {
  protected lastQuery?: Query<DocumentData, DocumentData>
  protected lastSnapshot?: QuerySnapshot<DataType, DocumentData>
  private currentIndex = 0

  private numberOfItems = 0
  private isLoadingMoreData = false

  protected FETCH_LIMIT = 20

  public clearSubscriberStates(): void {
    for (const subscriber of this.subscribers) {
      subscriber.setState([])
    }
  }

  override createControllerState(
    controller: Controller,
    sideEffect?: () => void,
  ): ControllerStateArray<DataType> {
    return new ControllerStateArray(controller, sideEffect)
  }

  override listenGroup(queries: Query[]) {
    this.unsubscribes.push(
      ...queries.map((query) => {
        const newIndex = this.currentIndex
        this.currentIndex++

        return onSnapshot(query.withConverter(this.converter), (snapshot) => {
          if (newIndex === this.currentIndex - 1) {
            this.lastSnapshot = snapshot
            this.isLoadingMoreData = false
          }

          this.updateInternalState.bind(this)(snapshot, newIndex)
        })
      }),
    )
  }

  override listen(query: Query): void {
    this.currentIndex = 0
    this.numberOfItems = 0
    this.lastQuery = query

    for (const subscriber of this.subscribers) {
      subscriber.clear()
    }

    this.unsubscribeListeners()
    this.lastQueries.set(this.activePage, [query])
    this.listenGroup([query])
  }

  public listenNext(query: Query): void {
    const lastQueriesForPage = this.lastQueries.get(this.activePage)

    if (lastQueriesForPage === undefined) {
      throw new Error(
        'You must call listen before calling listenNext to set the initial query.',
      )
    }

    this.lastQueries.set(this.activePage, [...lastQueriesForPage, query])
    this.listenGroup([query])
  }

  protected parseToReturnType(
    snapshot: QuerySnapshot<DataType, DocumentData>,
    index: number,
  ): DataType[] {
    const newData = []

    for (const doc of snapshot.docs) {
      newData.push(doc.data())
    }

    this.cachedData.set(index, newData)

    const allData = Array.from(this.cachedData.values()).flat()
    const uniqueData = Array.from(
      new Map(allData.map(item => [item.id, item])).values(),
    )

    this.numberOfItems = uniqueData.length

    return uniqueData
  }

  public canLoadMore(): boolean {
    if (this.isLoadingMoreData) {
      return false
    }

    return !!(
      this.lastSnapshot
      && this.lastQuery
      && this.lastSnapshot.docs.length === this.FETCH_LIMIT
    )
  }

  public loadNextDataIfPossible(last: number): boolean {
    if (!this.canLoadMore()) {
      return false
    }

    const isWithinLoadedRange = last < this.numberOfItems

    if (isWithinLoadedRange) {
      return false
    }

    this.isLoadingMoreData = true

    const lastDoc = this.lastSnapshot!.docs[this.lastSnapshot!.docs.length - 1]
    const nextQuery = query(this.lastQuery!, startAfter(lastDoc))

    this.listenNext(nextQuery)

    return true
  }

  protected applyDateFilter(listenQuery: Query, { from, to }: DateRange) {
    let updatedQuery: Query | undefined = undefined

    if (from && to) {
      updatedQuery = query(
        listenQuery,
        and(where('Created', '>=', from), where('Created', '<=', to)),
      )
    }

    return updatedQuery ?? listenQuery
  }
}
